// src/client/index.ts
import { Core } from "@walletconnect/core";
import {
  DEFAULT_KEYSERVER_URL,
  NotifyClient
} from "@walletconnect/notify-client";
import { proxy, subscribe } from "valtio";
import { proxySet } from "valtio/utils";

// src/utils/promiseTimeout.ts
var createPromiseWithTimeout = (promise, timeoutMs, actionName) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(`${actionName} timed out after ${timeoutMs} milliseconds`)
      );
    }, timeoutMs);
    promise.then(resolve).catch(reject).finally(() => clearTimeout(timeoutId));
  });
};

// src/utils/address.ts
var fetchByteCodeFromRpc = async (address, chainId, rpcUrlBuilder) => {
  const rpcUrl = rpcUrlBuilder(chainId);
  try {
    const result = await fetch(
      rpcUrl,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          jsonrpc: "2.0",
          method: "eth_getCode",
          params: [address, "latest"],
          id: Date.now()
        })
      }
    ).then((response) => response.json());
    return result;
  } catch (e) {
    throw new Error(
      `Failed to fetch bytecode of address ${address} from RPC ${rpcUrl}. Error: ${e.message}`
    );
  }
};
var isSmartWallet = async (address, chainId, rpcUrlBuilder) => {
  const { result: bytecode } = await fetchByteCodeFromRpc(
    address,
    chainId,
    rpcUrlBuilder
  );
  const nonContractBytecode = !bytecode || bytecode === "0x" || bytecode === "0x0" || bytecode === "0x00";
  return !nonContractBytecode;
};

// src/client/index.ts
var DEFAULT_RPC_URL = "https://rpc.walletconnect.com/v1/";
var ECHO_URL = "https://echo.walletconnect.com";
var _Web3InboxClient = class _Web3InboxClient {
  constructor(notifyClient, domain, allApps, rpcUrlBuilder, projectId) {
    this.notifyClient = notifyClient;
    this.domain = domain;
    this.allApps = allApps;
    this.rpcUrlBuilder = rpcUrlBuilder;
    this.projectId = projectId;
  }
  getRequiredAccountParam(account) {
    if (account) {
      return account;
    } else if (_Web3InboxClient.clientState.account) {
      return _Web3InboxClient.clientState.account;
    } else {
      return;
    }
  }
  attachEventListeners() {
    const updateInternalSubscriptions = () => {
      _Web3InboxClient.subscriptionState.subscriptions = this.notifyClient.subscriptions.getAll();
    };
    this.notifyClient.on("notify_delete", updateInternalSubscriptions);
    this.notifyClient.on("notify_subscription", updateInternalSubscriptions);
    this.notifyClient.on("notify_update", updateInternalSubscriptions);
    this.notifyClient.on(
      "notify_subscriptions_changed",
      updateInternalSubscriptions
    );
    subscribe(_Web3InboxClient.clientState, () => {
      updateInternalSubscriptions();
    });
    const clientReadyInterval = setInterval(() => {
      if (this.notifyClient.hasFinishedInitialLoad()) {
        updateInternalSubscriptions();
        clearInterval(clientReadyInterval);
      }
    }, 100);
  }
  /**
   * Get singleton instance of Web3InboxClient
   */
  static getInstance() {
    if (_Web3InboxClient.clientState.isReady && _Web3InboxClient.instance) {
      return _Web3InboxClient.instance;
    } else {
      throw new Error(
        "Client not ready. getInstance can only be called when client is ready. Consider using getIsReady"
      );
    }
  }
  /**
   * Get if singleton instance of Web3InboxClient is ready for use
   */
  static getIsReady() {
    return _Web3InboxClient.clientState.isReady;
  }
  /**
   * Watch if singleton instance of Web3InboxClient is ready for use
   *
   * @param cb - Callback that gets called when isReady updates
   */
  static watchIsReady(cb) {
    cb(_Web3InboxClient.clientState.isReady);
    return subscribe(_Web3InboxClient.clientState, () => {
      cb(_Web3InboxClient.clientState.isReady);
    });
  }
  /**
   * Sets account. If identity has been previously registered, it sets that too.
   *
   * @param {string} account
   */
  async setAccount(account) {
    const isRegistered = await this.getAccountIsRegistered(account);
    if (isRegistered) {
      const identity = await this.notifyClient.identityKeys.getIdentity({
        account
      });
      _Web3InboxClient.clientState.registration = { account, identity };
      _Web3InboxClient.clientState.account = account;
    } else {
      _Web3InboxClient.clientState.registration = void 0;
      _Web3InboxClient.clientState.account = account;
    }
  }
  /**
   * Retrieve set account.
   *
   * @returns {string|undefined} Set account
   */
  getAccount() {
    return _Web3InboxClient.clientState.account;
  }
  /**
   * Watch account as it changes.
   *
   * @param cb - Callback that gets called when account updates
   */
  watchAccount(cb) {
    cb(_Web3InboxClient.clientState.account);
    return subscribe(_Web3InboxClient.clientState, () => {
      return cb(_Web3InboxClient.clientState.account);
    });
  }
  /**
   * Get if account has a registered identity
   *
   * @param {string} account - account to check identity for
   *
   * @returns {boolean} isRegistered
   */
  async getAccountIsRegistered(account) {
    try {
      return this.notifyClient.isRegistered({
        account,
        allApps: this.allApps,
        domain: this.domain
      });
    } catch (e) {
      return false;
    }
  }
  /**
   * Watch account's identity registration status
   *
   * @param {string} account - account to watch identity for
   * @param cb - callback that gets called when registration status updates
   */
  watchAccountIsRegistered(account, cb) {
    this.getAccountIsRegistered(account).then(cb);
    return subscribe(_Web3InboxClient.clientState, async () => {
      cb(await this.getAccountIsRegistered(account));
    });
  }
  /**
   * Init a singleton instance of the Web3InboxClient
   *
   * @param {Object} params - the params needed to init the client
   * @param {string} params.projectId - your WalletConnect Cloud project ID
   * @param {string} params.domain - The domain of the default dapp to target for functions.
   * @param {boolean} params.allApps - All account's subscriptions accessable if explicitly set to true. Only param.domain's otherwise
   *
   * @returns {Object} Web3InboxClient
   */
  static async init(params) {
    if (_Web3InboxClient.clientState.initting) {
      return new Promise((res) => {
        subscribe(_Web3InboxClient.clientState, () => {
          if (!_Web3InboxClient.clientState.isReady) {
            res(_Web3InboxClient.instance);
          }
        });
      });
    }
    if (_Web3InboxClient.instance)
      return _Web3InboxClient.instance;
    _Web3InboxClient.clientState.initting = true;
    const core = new Core({
      customStoragePrefix: "w3i-core",
      projectId: params.projectId
    });
    const notifyParams = {
      core,
      keyserverUrl: DEFAULT_KEYSERVER_URL,
      projectId: params.projectId,
      logger: params.logLevel ?? "error"
    };
    const notifyClient = await NotifyClient.init(notifyParams);
    _Web3InboxClient.instance = new _Web3InboxClient(
      notifyClient,
      params.domain ?? window.location.host,
      // isLimited is defaulted to true, therefore null/undefined values are defaulted to true.
      params.allApps ?? false,
      params.rpcUrlBuilder ?? ((chainId) => `${DEFAULT_RPC_URL}?projectId=${params.projectId}&chainId=${chainId}`),
      params.projectId
    );
    _Web3InboxClient.subscriptionState.subscriptions = notifyClient.subscriptions.getAll();
    _Web3InboxClient.instance.attachEventListeners();
    _Web3InboxClient.clientState.initting = false;
    _Web3InboxClient.clientState.isReady = true;
    return _Web3InboxClient.instance;
  }
  /**
   * Retrieve the subscription object
   *
   * @param {string} [account] - Account to get subscription for, defaulted to current account
   * @param {string} [domain] - Domain to get subscription for, defaulted to one set in init.
   *
   * @returns {Object} Subscription Object
   */
  getSubscription(account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    const domainToSearch = domain ?? this.domain;
    if (!accountOrInternalAccount) {
      return null;
    }
    const subscription = _Web3InboxClient.subscriptionState.subscriptions.find((sub) => {
      const accountMatch = sub.account === accountOrInternalAccount;
      const domainMatch = sub.metadata.appDomain === domainToSearch;
      return accountMatch && domainMatch;
    }) ?? null;
    return subscription;
  }
  /**
   * Retrieve the subscription object
   *
   * @param {string} [account] - Account to get subscriptions for, defaulted to current account
   *
   * @returns {Object[]} Subscription Objects
   */
  getSubscriptions(account) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      return [];
    }
    return _Web3InboxClient.subscriptionState.subscriptions.filter((sub) => {
      const accountMatch = sub.account === accountOrInternalAccount;
      return accountMatch;
    });
  }
  /**
   * Watch the subscription for an account and domain combination
   *
   * @param cb - Callback that gets called with the subscription every time it is updated
   * @param {string} [account] - Account to watch subscription for, defaulted to current account
   * @param {string} [domain] - Domain to watch subscription for, defaulted to one set in init.
   */
  watchSubscription(cb, account, domain) {
    cb(this.getSubscription(account, domain));
    return subscribe(_Web3InboxClient.subscriptionState, () => {
      cb(this.getSubscription(account, domain));
    });
  }
  /**
   * Watch the subscriptions for an account
   *
   * @param cb - Callback that gets called with the subscriptions every time they are updated
   * @param {string} [account] - Account to watch subscriptions for, defaulted to current account
   */
  watchSubscriptions(cb, account) {
    cb(this.getSubscriptions(account));
    return subscribe(_Web3InboxClient.subscriptionState, () => {
      cb(this.getSubscriptions(account));
    });
  }
  /**
   * Update a subscriptions for an account
   *
   * @param {string[]} scope  - Active scopes array
   * @param {string} [account] - Account to update subscription for, defaulted to current account
   * @param {string} [domain] - Domain of subscription to update, defaulted to one set in init.
   */
  async update(scope, account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      return false;
    }
    const sub = this.getSubscription(account, domain);
    if (sub) {
      return createPromiseWithTimeout(
        this.notifyClient.update({
          topic: sub.topic,
          scope
        }),
        _Web3InboxClient.maxTimeout,
        "update"
      );
    }
    return false;
  }
  /**
   * Get notification types for a subscription
   *
   * @param {string} [account] - Account to get subscription notification types for, defaulted to current account
   * @param {string} [domain] - Domain to get subscription notification types for, defaulted to one set in init.
   *
   * @returns {Object[]} scope map  - Active scopes array
   */
  getNotificationTypes(account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      return {};
    }
    const sub = this.getSubscription(account, domain);
    if (sub) {
      return sub.scope;
    }
    return {};
  }
  pageNotifications(notificationsPerPage, isInfiniteScroll, account, domain) {
    const data = proxy({
      notifications: [],
      hasMore: false
    });
    const currentNotificationIds = proxySet([]);
    const notifyMessageListener = async () => {
      const fetchedNotificationData = await this.getNotificationHistory(
        notificationsPerPage,
        void 0,
        account,
        domain
      );
      const notification = fetchedNotificationData.notifications.shift();
      if (notification && !currentNotificationIds.has(notification.id)) {
        data.notifications = [notification, ...data.notifications];
        currentNotificationIds.add(notification.id);
      }
    };
    this.notifyClient.on("notify_message", notifyMessageListener);
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    const nextPage = async () => {
      const lastMessage = data.notifications.length ? data.notifications[data.notifications.length - 1].id : void 0;
      const fetchedNotificationData = await this.getNotificationHistory(
        notificationsPerPage,
        lastMessage,
        accountOrInternalAccount,
        domain ?? this.domain
      );
      data.notifications = isInfiniteScroll ? [
        ...data.notifications,
        ...fetchedNotificationData.notifications.filter(
          (n) => !currentNotificationIds.has(n.id)
        )
      ] : fetchedNotificationData.notifications;
      for (const notification of fetchedNotificationData.notifications) {
        currentNotificationIds.add(notification.id);
      }
      data.hasMore = fetchedNotificationData.hasMore;
    };
    nextPage();
    return (onNotificationDataUpdate) => {
      const unsub = subscribe(data, () => {
        onNotificationDataUpdate(data);
      });
      return {
        stopWatchingNotifications: () => {
          this.notifyClient.off("notify_message", notifyMessageListener);
          unsub();
        },
        data,
        nextPage
      };
    };
  }
  /**
   * Get message history for a subscription
   *
   * @param {number} limit - How many notifications to get after `startingAfter`
   * @param {string} [startingAfter] - ID of the notification to get messages after
   * @param {string} [account] - Account to get subscription message history for, defaulted to current account
   * @param {string} [domain] - Domain to get subscription message history for, defaulted to one set in init.
   *
   * @returns {Object[]} messages  - Message Record array
   */
  getNotificationHistory(limit, startingAfter, account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      return Promise.reject("No account configured");
    }
    const sub = this.getSubscription(
      accountOrInternalAccount,
      domain ?? this.domain
    );
    if (sub) {
      try {
        return createPromiseWithTimeout(
          this.notifyClient.getNotificationHistory({
            topic: sub.topic,
            limit,
            startingAfter
          }),
          _Web3InboxClient.maxTimeout,
          "getNotificationHistory"
        );
      } catch (e) {
        console.error("Failed to fetch messages", e);
        return Promise.reject({
          hasMore: false,
          notifications: []
        });
      }
    } else {
      return Promise.reject(
        `No sub found for account ${account} and domain ${domain ?? this.domain}`
      );
    }
  }
  /**
   * Prepare a registration for the register function
   *
   * @param {Object} params - register params
   * @param {string} params.account - Account to register.
   *
   * @returns {Object} preparedRegistration - Prepared Registration
   */
  prepareRegistration(params) {
    return createPromiseWithTimeout(
      this.notifyClient.prepareRegistration({
        account: params.account,
        domain: this.domain,
        allApps: this.allApps
      }),
      _Web3InboxClient.maxTimeout,
      "prepareRegistration"
    );
  }
  /**
   * Register account on keyserver, allowing them to subscribe
   *
   * @param {Object} params - register params
   * @param {string} params.registerParams - Prepared params for registration
   * @param {string} params.signature - Signature of prepared message
   *
   * @returns {string} identityKey  - Registered identity
   */
  async register(params) {
    try {
      const account = params.registerParams.cacaoPayload.iss.split(":").slice(-3).join(":");
      const [chainPrefix, chain, address] = account.split(":");
      const projectId = this.notifyClient?.core?.projectId;
      if (!projectId) {
        throw new Error("Project ID needs to be supplied");
      }
      const isEip1271Signature = await isSmartWallet(
        address,
        `${chainPrefix}:${chain}`,
        this.rpcUrlBuilder
      );
      const registeredIdentity = await createPromiseWithTimeout(
        this.notifyClient.register({
          registerParams: params.registerParams,
          signature: params.signature,
          signatureType: isEip1271Signature ? "eip1271" : "eip191"
        }),
        _Web3InboxClient.maxTimeout,
        "register"
      );
      _Web3InboxClient.clientState.registration = {
        account,
        identity: registeredIdentity
      };
      _Web3InboxClient.clientState.account = account;
      return registeredIdentity;
    } catch (e) {
      throw new Error(`Failed to register: ${e.message}`);
    }
  }
  /**
   * Unregister account on keyserver and unsubscribe from all topics
   *
   * @param {Object} params - register params
   * @param {string} params.account - Account to unregister.
   *
   */
  async unregister(params) {
    try {
      await createPromiseWithTimeout(
        this.notifyClient.unregister(params),
        _Web3InboxClient.maxTimeout,
        "unregister"
      );
      _Web3InboxClient.clientState.registration = void 0;
    } catch (e) {
      throw new Error(`Failed to uregister: ${e.message}`);
    }
  }
  /**
   * Check if account is subscribed to a dapp
   *
   * @param {string} [account] - Account to check subscription status of , defaulted to current account
   * @param {string} [domain] - Domain to get subscription status of, defaulted to one set in init.
   *
   * @returns {boolean} isSubscribed
   */
  isSubscribedToDapp(account, domain) {
    const sub = this.getSubscription(account, domain);
    return Boolean(sub);
  }
  /**
   * Subscribe to a dapp
   *
   * @param {string} [account] - Account to subscribe with, defaulted to current account
   * @param {string} [domain] - Domain to subscribe to, defaulted to one set in init.
   *
   */
  async subscribeToDapp(account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      console.error("Failed to subscribe since no account has been set");
      return;
    }
    if (this.isSubscribedToDapp(accountOrInternalAccount, domain)) {
      return;
    }
    await createPromiseWithTimeout(
      this.notifyClient.subscribe({
        account: accountOrInternalAccount,
        appDomain: domain ?? this.domain
      }),
      _Web3InboxClient.maxTimeout,
      "subscribe"
    );
  }
  /**
   * Unsubscribe from a dapp
   *
   * @param {string} [account] - Account to unsubscribe with, defaulted to current account
   * @param {string} [domain] - Domain to unsubscribe from, defaulted to one set in init.
   *
   */
  async unsubscribeFromDapp(account, domain) {
    const accountOrInternalAccount = this.getRequiredAccountParam(account);
    if (!accountOrInternalAccount) {
      console.error("Failed to unsubscribe since no account has been set");
      return;
    }
    const sub = this.getSubscription(accountOrInternalAccount, domain);
    if (sub) {
      await createPromiseWithTimeout(
        this.notifyClient.deleteSubscription({ topic: sub.topic }),
        _Web3InboxClient.maxTimeout,
        "deleteSubscription"
      );
    }
  }
  /**
   * Register with a push server to allow for push notifications.
   *
   * @param {string} token - APNS or FCM token.
   * @param {string} [type] - Whether the token is APNS or FCM. Defaulted to fcm.
   *
   * @returns {string} clientId - Client ID that successfully registered with echo
   */
  async registerWithPushServer(token, type = "fcm") {
    const clientId = await this.notifyClient.core.crypto.getClientId();
    const echoUrl = `${ECHO_URL}/${this.projectId}/clients`;
    const echoResponse = await fetch(echoUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        client_id: clientId,
        type,
        token
      })
    });
    if (echoResponse.status === 200) {
      return clientId;
    }
    throw new Error(`Registration with push server failed, status: ${echoResponse.status}, response: ${await echoResponse.text()}`);
  }
  /**
   * Watch if account is subscribed to a dapp
   *
   * @param cb - callback that gets called every time isSubbed status changes
   * @param {string} [account] - Account to watch subscriptions for, defaulted to current account
   * @param {string} [domain] - Domain to watch subscriptions for, defaulted to one set in init.
   *
   */
  watchIsSubscribed(cb, account, domain) {
    cb(this.isSubscribedToDapp(account, domain));
    return subscribe(_Web3InboxClient.subscriptionState, () => {
      cb(this.isSubscribedToDapp(account, domain));
    });
  }
  /**
   * Watch scope map (notification types) for a subscription
   *
   * @param cb - callback that gets called every time the scope map changes
   * @param {string} [account] - Account to watch subscription's scope map for, defaulted to current account
   * @param {string} [domain] - Domain to watch subscription's scope map for, defaulted to one set in init.
   *
   */
  watchNotificationTypes(cb, account, domain) {
    cb(this.getSubscription(account, domain)?.scope ?? {});
    return subscribe(_Web3InboxClient.subscriptionState, () => {
      cb(this.getSubscription(account, domain)?.scope ?? {});
    });
  }
  on(e, listener) {
    this.notifyClient.on(e, listener);
  }
  off(e, listener) {
    this.notifyClient.off(e, listener);
  }
  once(e, listener) {
    this.notifyClient.once(e, listener);
  }
};
_Web3InboxClient.maxTimeout = 1e4;
_Web3InboxClient.instance = null;
_Web3InboxClient.subscriptionState = proxy({ subscriptions: [], messages: [] });
_Web3InboxClient.clientState = proxy({
  isReady: false,
  initting: false,
  account: void 0,
  registration: void 0
});
var Web3InboxClient = _Web3InboxClient;

// src/index.ts
import * as valtio from "valtio";
export {
  Web3InboxClient,
  valtio
};
